import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSkeleton,
  Modal,
  Pagination,
  Select,
} from '@nexora/design-system';
import { formatCurrency } from '../../../shared/utils/format';
import {
  APPROVAL_CONDITIONS,
  type ApprovalCondition,
  type ApprovalRule,
  type ApprovalRuleInput,
} from './types';
import {
  useApprovalRules,
  useCreateApprovalRule,
  useDeleteApprovalRule,
  useResolveApprovalRule,
  useUpdateApprovalRule,
} from './useContractQueries';

/* ── Configuração de Regras de Alçada (R2C, tela 9)
 * Consome CRUD /approval-rules (GET list, POST, PATCH /:id, DELETE /:id) e
 * GET /approval-rules/resolve (resolução por documento/valor/centro de custo).
 * Protótipo: Tipo Documento, Centro Custo, Condição, Nível Aprovação, Cargo,
 * Limite Financeiro, Status, Ações.
 */

const DOCUMENT_TYPE_OPTIONS = [
  'PEDIDO_VENDA',
  'PEDIDO_COMPRA',
  'CONTRATO',
  'NF_SAIDA',
  'NF_ENTRADA',
  'SOLICITACAO_PAGAMENTO',
  'ORCAMENTO',
];

const DOCUMENT_TYPE_LABEL: Record<string, string> = {
  PEDIDO_VENDA: 'Pedido de Venda',
  PEDIDO_COMPRA: 'Pedido de Compra',
  CONTRATO: 'Contrato',
  NF_SAIDA: 'Nota Fiscal de Saída',
  NF_ENTRADA: 'Nota Fiscal de Entrada',
  SOLICITACAO_PAGAMENTO: 'Solicitação de Pagamento',
  ORCAMENTO: 'Orçamento',
};

const CONDITION_LABEL: Record<ApprovalCondition, string> = {
  ALWAYS: 'Sempre',
  VALUE_GT: 'Valor acima de',
  VALUE_BETWEEN: 'Valor entre',
};

function conditionText(rule: ApprovalRule): string {
  if (rule.condition === 'ALWAYS') return 'Sempre';
  if (rule.condition === 'VALUE_GT') {
    return `Valor > ${formatCurrency(rule.minValue ?? 0)}`;
  }
  if (rule.condition === 'VALUE_BETWEEN') {
    return `Valor entre ${formatCurrency(rule.minValue ?? 0)} e ${formatCurrency(
      rule.maxValue ?? 0,
    )}`;
  }
  return rule.condition;
}

function docTypeLabel(value: string): string {
  return DOCUMENT_TYPE_LABEL[value] ?? value;
}

const EMPTY_FORM: ApprovalRuleInput = {
  documentType: 'PEDIDO_VENDA',
  costCenter: null,
  condition: 'VALUE_GT',
  minValue: null,
  maxValue: null,
  approvalLevel: 1,
  roleTitle: '',
  financialLimit: null,
  active: true,
  description: '',
};

interface FormState {
  documentType: string;
  costCenter: string;
  condition: ApprovalCondition;
  minValue: string;
  maxValue: string;
  approvalLevel: string;
  roleTitle: string;
  financialLimit: string;
  active: boolean;
  description: string;
}

function toForm(rule: ApprovalRule | null): FormState {
  return {
    documentType: rule?.documentType ?? EMPTY_FORM.documentType,
    costCenter: rule?.costCenter ?? '',
    condition: (rule?.condition as ApprovalCondition) ?? EMPTY_FORM.condition,
    minValue: rule?.minValue !== null && rule?.minValue !== undefined ? String(rule.minValue) : '',
    maxValue: rule?.maxValue !== null && rule?.maxValue !== undefined ? String(rule.maxValue) : '',
    approvalLevel: String(rule?.approvalLevel ?? 1),
    roleTitle: rule?.roleTitle ?? '',
    financialLimit:
      rule?.financialLimit !== null && rule?.financialLimit !== undefined
        ? String(rule.financialLimit)
        : '',
    active: rule?.active ?? true,
    description: rule?.description ?? '',
  };
}

export function ApprovalRulesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');
  const [docFilter, setDocFilter] = useState('');

  const { data, isLoading, error } = useApprovalRules({
    page,
    limit: 10,
    ...(activeFilter !== 'all' ? { active: activeFilter === 'active' } : {}),
    ...(docFilter ? { documentType: docFilter } : {}),
  });
  const createRule = useCreateApprovalRule();
  const updateRule = useUpdateApprovalRule();
  const deleteRule = useDeleteApprovalRule();

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ApprovalRule | null>(null);
  const [form, setForm] = useState<FormState>(toForm(null));
  const [formError, setFormError] = useState('');
  const [showDelete, setShowDelete] = useState<ApprovalRule | null>(null);

  /* Resolver alçada */
  const [resolveDoc, setResolveDoc] = useState('PEDIDO_VENDA');
  const [resolveValue, setResolveValue] = useState('');
  const [resolveCostCenter, setResolveCostCenter] = useState('');
  const [resolveQuery, setResolveQuery] = useState<{
    documentType: string;
    value: number;
    costCenter?: string;
  } | null>(null);
  const resolveQueryResult = useResolveApprovalRule(resolveQuery);

  const rows = data?.data ?? [];
  const totalPages = useMemo(() => Math.max(1, Math.ceil((data?.total ?? 0) / 10)), [data?.total]);

  const setField = <K extends keyof FormState>(key: K, val: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const openNew = (): void => {
    setEditingRule(null);
    setForm(toForm(null));
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (rule: ApprovalRule): void => {
    setEditingRule(rule);
    setForm(toForm(rule));
    setFormError('');
    setShowForm(true);
  };

  const submitForm = (): void => {
    if (!form.documentType.trim()) {
      setFormError('Informe o tipo de documento.');
      return;
    }
    if (!form.roleTitle.trim()) {
      setFormError('Informe o cargo do aprovador.');
      return;
    }
    const level = Number(form.approvalLevel);
    if (!Number.isInteger(level) || level < 1) {
      setFormError('Nível de aprovação deve ser inteiro ≥ 1.');
      return;
    }
    const minV = form.minValue === '' ? null : Number(form.minValue);
    const maxV = form.maxValue === '' ? null : Number(form.maxValue);
    const finLim = form.financialLimit === '' ? null : Number(form.financialLimit);
    if (form.condition !== 'ALWAYS' && (minV === null || !Number.isFinite(minV) || minV < 0)) {
      setFormError('Condição por valor exige limite mínimo ≥ 0.');
      return;
    }
    if (form.condition === 'VALUE_BETWEEN' && (maxV === null || !Number.isFinite(maxV) || maxV < 0)) {
      setFormError('Condição entre valores exige limite máximo ≥ 0.');
      return;
    }
    if (finLim !== null && (!Number.isFinite(finLim) || finLim < 0)) {
      setFormError('Limite financeiro deve ser ≥ 0.');
      return;
    }
    setFormError('');
    const payload: ApprovalRuleInput = {
      documentType: docTypeLabel(form.documentType.trim()),
      condition: form.condition,
      approvalLevel: level,
      roleTitle: form.roleTitle.trim(),
      active: form.active,
      ...(form.costCenter.trim() ? { costCenter: form.costCenter.trim() } : { costCenter: null }),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
      ...(form.condition === 'VALUE_GT' || form.condition === 'VALUE_BETWEEN'
        ? { minValue: minV ?? 0 }
        : {}),
      ...(form.condition === 'VALUE_BETWEEN' ? { maxValue: maxV ?? 0 } : {}),
      ...(finLim !== null ? { financialLimit: finLim } : { financialLimit: null }),
    };
    if (editingRule) {
      updateRule.mutate(
        { ruleId: editingRule.id, input: payload },
        {
          onSuccess: () => setShowForm(false),
          onError: (err) => setFormError((err as Error).message),
        },
      );
    } else {
      createRule.mutate(payload, {
        onSuccess: () => setShowForm(false),
        onError: (err) => setFormError((err as Error).message),
      });
    }
  };

  const toggleActive = (rule: ApprovalRule): void => {
    updateRule.mutate({ ruleId: rule.id, input: { active: !rule.active } });
  };

  const doResolve = (): void => {
    const value = Number(resolveValue);
    if (resolveValue === '' || !Number.isFinite(value) || value < 0) return;
    setResolveQuery({
      documentType: docTypeLabel(resolveDoc),
      value,
      ...(resolveCostCenter.trim() ? { costCenter: resolveCostCenter.trim() } : {}),
    });
  };

  if (isLoading) {
    return (
      <div className="p-xl">
        <LoadingSkeleton count={4} height="64px" />
      </div>
    );
  }

  return (
    <div className="p-xl flex flex-col gap-lg">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-md">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
            Configuração de Regras de Alçada
          </h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Defina limites financeiros e hierarquias de aprovação por tipo de
            documento.
          </p>
        </div>
        <Button variant="primary" onClick={openNew}>
          Nova regra
        </Button>
      </div>

      {error ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">
            {(error as Error).message}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-lg xl:grid-cols-3">
          {/* Lista de regras */}
          <div className="flex flex-col gap-md xl:col-span-2">
            <Card padding="0">
              <div className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md">
                <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
                  Regras Ativas
                </h3>
                <div className="flex flex-wrap items-center gap-sm">
                  <div className="w-44">
                    <Select
                      aria-label="Filtrar por status"
                      selectSize="sm"
                      value={activeFilter}
                      onChange={(e) => {
                        setActiveFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="all">Todos os status</option>
                      <option value="active">Somente ativas</option>
                      <option value="inactive">Somente inativas</option>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Select
                      aria-label="Filtrar por tipo de documento"
                      selectSize="sm"
                      value={docFilter}
                      onChange={(e) => {
                        setDocFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">Todos os documentos</option>
                      {DOCUMENT_TYPE_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {docTypeLabel(d)}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              {rows.length === 0 ? (
                <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">
                  Nenhuma regra de alçada cadastrada para este filtro.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left font-body-sm">
                    <thead className="bg-surface font-label-sm text-on-surface-variant">
                      <tr>
                        <th className="p-md font-medium">Tipo de Documento</th>
                        <th className="p-md font-medium">Centro de Custo</th>
                        <th className="p-md font-medium">Condição</th>
                        <th className="p-md font-medium">Nível</th>
                        <th className="p-md font-medium">Cargo</th>
                        <th className="p-md font-medium text-right">Limite Financeiro</th>
                        <th className="p-md font-medium">Status</th>
                        <th className="p-md font-medium text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((rule) => (
                        <tr
                          key={rule.id}
                          className="border-t border-outline-variant/60 hover:bg-surface-container-highest"
                        >
                          <td className="p-md font-label-md text-on-surface">
                            {docTypeLabel(rule.documentType)}
                          </td>
                          <td className="p-md">{rule.costCenter ?? '—'}</td>
                          <td className="p-md text-on-surface-variant">
                            {conditionText(rule)}
                          </td>
                          <td className="p-md text-center">{rule.approvalLevel}</td>
                          <td className="p-md">{rule.roleTitle}</td>
                          <td className="p-md text-right tabular-nums">
                            {rule.financialLimit !== null
                              ? formatCurrency(rule.financialLimit)
                              : 'Ilimitado'}
                          </td>
                          <td className="p-md">
                            <Badge variant={rule.active ? 'success' : 'neutral'}>
                              {rule.active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </td>
                          <td className="p-md">
                            <div className="flex items-center justify-end gap-sm">
                              <button
                                type="button"
                                className="font-label-md text-label-md text-primary hover:underline"
                                onClick={() => openEdit(rule)}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="font-label-md text-label-md text-warning-amber hover:underline"
                                disabled={updateRule.isPending}
                                onClick={() => toggleActive(rule)}
                              >
                                {rule.active ? 'Desativar' : 'Ativar'}
                              </button>
                              <button
                                type="button"
                                className="font-label-md text-label-md text-error hover:underline"
                                disabled={deleteRule.isPending}
                                onClick={() => setShowDelete(rule)}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 ? (
                <div className="flex items-center justify-between border-t border-outline-variant p-sm">
                  <span className="font-body-sm text-body-sm text-on-surface-variant">
                    {data?.total ?? 0} regra{(data?.total ?? 0) === 1 ? '' : 's'}
                  </span>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              ) : null}
            </Card>
          </div>

          {/* Resolver alçada + config globais */}
          <div className="flex flex-col gap-lg">
            <Card>
              <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
                Resolver alçada
              </h3>
              <p className="mb-sm mt-0 font-body-sm text-body-sm text-on-surface-variant">
                Informe documento, valor e centro de custo para saber o nível de
                aprovação exigido.
              </p>
              <div className="flex flex-col gap-md">
                <div>
                  <div className="field-label mb-xs">Tipo de documento</div>
                  <Select
                    aria-label="Tipo de documento"
                    value={resolveDoc}
                    onChange={(e) => setResolveDoc(e.target.value)}
                  >
                    {DOCUMENT_TYPE_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {docTypeLabel(d)}
                      </option>
                    ))}
                  </Select>
                </div>
                <Input
                  id="resolve-value"
                  label="Valor do documento (R$)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={resolveValue}
                  onChange={(e) => setResolveValue(e.target.value)}
                />
                <Input
                  id="resolve-costcenter"
                  label="Centro de custo"
                  value={resolveCostCenter}
                  onChange={(e) => setResolveCostCenter(e.target.value)}
                  placeholder="Ex.: TI / Infra"
                />
                <Button variant="primary" onClick={doResolve}>
                  Resolver
                </Button>
              </div>

              {resolveQueryResult.isLoading ? (
                <div className="mt-md">
                  <LoadingSkeleton count={2} height="40px" />
                </div>
              ) : resolveQueryResult.error ? (
                <p className="mb-0 mt-md font-body-sm text-body-sm text-error">
                  {(resolveQueryResult.error as Error).message}
                </p>
              ) : resolveQueryResult.data ? (
                <div className="mt-md rounded-lg border border-outline-variant p-sm">
                  <div className="mb-sm flex items-center gap-sm">
                    <Badge variant={resolveQueryResult.data.requiresApproval ? 'warning' : 'success'}>
                      {resolveQueryResult.data.requiresApproval
                        ? `Exige ${resolveQueryResult.data.requiredLevels} nível(níveis)`
                        : 'Sem aprovação exigida'}
                    </Badge>
                  </div>
                  {resolveQueryResult.data.rules.length === 0 ? (
                    <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                      Nenhuma regra ativa se aplica a este documento/valor.
                    </p>
                  ) : (
                    <ul className="m-0 flex flex-col gap-xs p-0" style={{ listStyle: 'none' }}>
                      {resolveQueryResult.data.rules.map((r) => (
                        <li
                          key={r.ruleId}
                          className="flex items-center justify-between gap-sm font-body-sm text-body-sm"
                        >
                          <span className="text-on-surface">
                            Nível {r.approvalLevel} · {r.roleTitle}
                          </span>
                          <span className="text-on-surface-variant">
                            {r.financialLimit !== null
                              ? formatCurrency(r.financialLimit)
                              : 'Ilimitado'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </Card>

            <Card>
              <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
                Configurações globais
              </h3>
              <p className="mb-0 font-body-sm text-body-sm text-on-surface-variant">
                SLA de aprovação e notificações são gerenciados pela área de
                governança. As regras acima definem a hierarquia de alçada por
                documento.
              </p>
            </Card>
          </div>
        </div>
      )}

      {/* Modal nova/editar regra */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingRule ? 'Editar regra de alçada' : 'Nova regra de alçada'}
        size="lg"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={createRule.isPending || updateRule.isPending}
              onClick={submitForm}
            >
              Salvar regra
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            <div>
              <div className="field-label mb-xs">Tipo de documento *</div>
              <Select
                aria-label="Tipo de documento"
                value={form.documentType}
                onChange={(e) => setField('documentType', e.target.value)}
              >
                {DOCUMENT_TYPE_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {docTypeLabel(d)}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              id="rule-costcenter"
              label="Centro de custo"
              value={form.costCenter}
              onChange={(e) => setField('costCenter', e.target.value)}
              placeholder="Vazio = todos"
            />
            <div>
              <div className="field-label mb-xs">Condição *</div>
              <Select
                aria-label="Condição"
                value={form.condition}
                onChange={(e) => setField('condition', e.target.value as ApprovalCondition)}
              >
                {APPROVAL_CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {CONDITION_LABEL[c]}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              id="rule-level"
              label="Nível de aprovação *"
              type="number"
              min="1"
              step="1"
              value={form.approvalLevel}
              onChange={(e) => setField('approvalLevel', e.target.value)}
            />
            {form.condition === 'VALUE_GT' || form.condition === 'VALUE_BETWEEN' ? (
              <Input
                id="rule-minvalue"
                label="Valor mínimo (R$) *"
                type="number"
                min="0"
                step="0.01"
                value={form.minValue}
                onChange={(e) => setField('minValue', e.target.value)}
              />
            ) : null}
            {form.condition === 'VALUE_BETWEEN' ? (
              <Input
                id="rule-maxvalue"
                label="Valor máximo (R$) *"
                type="number"
                min="0"
                step="0.01"
                value={form.maxValue}
                onChange={(e) => setField('maxValue', e.target.value)}
              />
            ) : null}
            <Input
              id="rule-role"
              label="Cargo do aprovador *"
              value={form.roleTitle}
              onChange={(e) => setField('roleTitle', e.target.value)}
              placeholder="Ex.: Diretor Financeiro (CFO)"
            />
            <Input
              id="rule-finlimit"
              label="Limite financeiro (R$)"
              type="number"
              min="0"
              step="0.01"
              value={form.financialLimit}
              onChange={(e) => setField('financialLimit', e.target.value)}
              placeholder="Vazio = ilimitado"
            />
          </div>
          <Input
            id="rule-description"
            label="Descrição"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Ex.: Aprovação de pedidos de compra acima de R$ 50 mil"
          />
          <label className="flex items-center gap-sm font-body-md text-body-md text-on-surface">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setField('active', e.target.checked)}
              className="h-4 w-4 rounded border-outline-variant text-primary"
            />
            Regra ativa
          </label>
          {formError ? (
            <p className="mb-0 font-body-sm text-body-sm text-error">{formError}</p>
          ) : null}
        </div>
      </Modal>

      {/* Modal excluir */}
      <Modal
        isOpen={showDelete !== null}
        onClose={() => setShowDelete(null)}
        title="Excluir regra de alçada?"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={deleteRule.isPending}
              onClick={() => {
                if (!showDelete) return;
                deleteRule.mutate(showDelete.id, { onSuccess: () => setShowDelete(null) });
              }}
            >
              Excluir
            </Button>
          </div>
        }
      >
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          {showDelete ? docTypeLabel(showDelete.documentType) : ''} ·{' '}
          {showDelete?.roleTitle ?? ''} será removida permanentemente.
        </p>
      </Modal>
    </div>
  );
}